import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["errorText", "confirmModal", "confirmMessage"]
  static values = {
    unreviewedSystemFlagCount: Number,
    unverifiedItemCount: Number,
    drawerUrl: String,
    autoVerifyUrl: String,
    autoAcceptSystemFlagsUrl: String,
    taskId: String
  }

  // Review requests made from this page (accept/reject/undo a flag, add a note) answer with a
  // re-rendered Complete Review button. Send them the task this page was opened with, so the
  // server re-renders the button for that task instead of guessing: a bill can have a coder
  // review and a medical necessity review open at once.
  connect() {
    this.tagFetchRequest = event => {
      if (!this.taskIdValue) return
      event.detail.fetchOptions.headers["X-Review-Task-Id"] = this.taskIdValue
    }
    document.addEventListener("turbo:before-fetch-request", this.tagFetchRequest)
  }

  disconnect() {
    document.removeEventListener("turbo:before-fetch-request", this.tagFetchRequest)
  }

  handleClick(event) {
    event.preventDefault()
    event.stopPropagation()

    // Hide any previous error text
    this.errorTextTarget.classList.add("hidden")
    this.errorTextTarget.textContent = ""

    if (this.unreviewedSystemFlagCountValue > 0) {
      const flagCount = this.unreviewedSystemFlagCountValue
      const verifyCount = this.unverifiedItemCountValue

      let message = `${flagCount} line ${flagCount === 1 ? "item has" : "items have"} system flags that haven't been reviewed.`
      if (verifyCount > 0) {
        message += ` Additionally, ${verifyCount} line ${verifyCount === 1 ? "item hasn't" : "items haven't"} been explicitly verified.`
      }
      message += " Do you want to auto-accept the remaining system flags and continue?"

      this.confirmMessageTarget.textContent = message
      this.confirmModalTarget.dataset.confirmAction = "acceptFlags"
      this.confirmModalTarget.classList.remove("hidden")
      return
    }

    if (this.unverifiedItemCountValue > 0) {
      const count = this.unverifiedItemCountValue
      const itemWord = count === 1 ? "line item hasn't" : "line items haven't"
      this.confirmMessageTarget.textContent =
        `${count} ${itemWord} been explicitly verified. Do you want to continue review completion and auto-verify those ${count} line ${count === 1 ? "item" : "items"}?`
      this.confirmModalTarget.dataset.confirmAction = "autoVerify"
      this.confirmModalTarget.classList.remove("hidden")
      return
    }

    // No issues — open the drawer normally
    this.openDrawer()
  }

  confirmAutoVerify(event) {
    event.preventDefault()
    this.confirmModalTarget.classList.add("hidden")

    const action = this.confirmModalTarget.dataset.confirmAction
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    if (action === "acceptFlags") {
      // First auto-accept system flags, then auto-verify if needed, then open drawer
      fetch(this.autoAcceptSystemFlagsUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        }
      }).then(response => {
        if (response.ok) {
          if (this.unverifiedItemCountValue > 0) {
            return fetch(this.autoVerifyUrlValue, {
              method: "POST",
              headers: {
                "X-CSRF-Token": csrfToken,
                "Accept": "application/json"
              }
            }).then(verifyResponse => {
              if (verifyResponse.ok) {
                this.openDrawer()
              } else {
                this.showError("Failed to auto-verify items. Please try again.")
              }
            })
          } else {
            this.openDrawer()
          }
        } else {
          this.showError("Failed to auto-accept system flags. Please try again.")
        }
      }).catch(() => {
        this.showError("Failed to auto-accept system flags. Please try again.")
      })
    } else {
      // Original auto-verify flow
      fetch(this.autoVerifyUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        }
      }).then(response => {
        if (response.ok) {
          this.openDrawer()
        } else {
          this.showError("Failed to auto-verify items. Please try again.")
        }
      }).catch(() => {
        this.showError("Failed to auto-verify items. Please try again.")
      })
    }
  }

  cancelConfirm(event) {
    event.preventDefault()
    this.confirmModalTarget.classList.add("hidden")
  }

  openDrawer() {
    const drawerEvent = new Event("drawer.open")
    drawerEvent.url = this.drawerUrlValue
    document.dispatchEvent(drawerEvent)
  }

  showError(message) {
    this.errorTextTarget.textContent = message
    this.errorTextTarget.classList.remove("hidden")
  }
}
